import React from 'react';
import { Modal } from 'antd';
import { MonitorPlay, X } from '@phosphor-icons/react';
import styled from 'styled-components';
import { radius } from '../../theme/radius';

export type ReadyModalProps = {
  open: boolean;
  onClose: () => void;
  onShare?: () => void;
  title: string;
  subtitle: string;
  viewLabel: string;
  shareLabel?: string;
};

export const ReadyModal: React.FC<ReadyModalProps> = ({
  open,
  onClose,
  onShare,
  title,
  subtitle,
  viewLabel,
  shareLabel,
}) => (
  <Modal
    open={open}
    footer={null}
    closable={false}
    maskClosable
    onCancel={onClose}
    width={720}
    styles={{ body: { padding: 0 } }}
    style={{ borderRadius: radius.box, overflow: 'hidden', maxWidth: '92vw' }}
    centered
  >
    <ModalBody>
      <LeftCol>
        <DotGrid />
        <IconWrapper>
          <MonitorPlay size={80} color="#0D9488" weight="light" />
        </IconWrapper>
      </LeftCol>
      <CloseBtn onClick={onClose} aria-label="Close">
        <X size={16} weight="bold" />
      </CloseBtn>
      <RightCol>
        <ModalTitle>{title}</ModalTitle>
        <ModalSubtitle>{subtitle}</ModalSubtitle>
        <ViewBtn onClick={onClose}>{viewLabel}</ViewBtn>
        {onShare && shareLabel && (
          <ShareLink onClick={onShare}>{shareLabel}</ShareLink>
        )}
      </RightCol>
    </ModalBody>
  </Modal>
);

const ModalBody = styled.div`
  display: flex;
  height: 440px;
  position: relative;

  @media (max-width: 600px) {
    flex-direction: column;
    height: auto;
  }
`;

const LeftCol = styled.div`
  width: 280px;
  flex-shrink: 0;
  height: 100%;
  background: linear-gradient(160deg, #ccfbf1 0%, #99f6e4 40%, #2dd4bf 100%);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 600px) {
    width: 100%;
    height: 140px;
    flex-shrink: 0;
  }
`;

const DotGrid = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.5) 1px,
    transparent 1px
  );
  background-size: 22px 22px;
`;

const IconWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const RightCol = styled.div`
  flex: 1;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 52px 48px;

  @media (max-width: 600px) {
    padding: 36px 28px 32px;
    align-items: center;
    text-align: center;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: ${radius.circle};
  border: none;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #e5e7eb;
    color: #090e19;
  }
`;

const ModalTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: #090e19;
  letter-spacing: -0.8px;
  line-height: 1.15;
  margin: 0 0 16px;

  @media (max-width: 600px) {
    font-size: 24px;
  }
`;

const ModalSubtitle = styled.p`
  font-family: 'Inter Variable', 'Inter', sans-serif;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.7;
  margin: 0 0 40px;

  @media (max-width: 600px) {
    margin: 0 0 28px;
  }
`;

const ViewBtn = styled.button`
  width: 100%;
  height: 48px;
  background: #14b8a6;
  color: white;
  border: none;
  border-radius: ${radius.pill}px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 14px;
  transition: background 0.15s;

  &:hover {
    background: #0d9488;
  }
`;

const ShareLink = styled.button`
  display: block;
  text-align: center;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  transition: color 0.15s;

  &:hover {
    color: #374151;
  }
`;
